// AmsInquiry.tsx — look a resident up, and see everything anyone has ever done to them.
//
// WHY THIS IS A SCREEN AND NOT A TAB ON THE CASE PAGE. The person who needs it most has a resident
// on the line right now and a block number in their ear. Reaching the history by filtering a work
// queue is not an answer to "Blk 317, unit 11-98, who called me last week?" — and until this
// existed, that was the only route.
//
// The search matches name, account number and address at once, unanchored, because a caller says
// whichever one they have and an agent should not have to know which box it belongs in.
//
// The history itself is the Touchpoints component the case record uses. One rendering of a
// resident's history, not two that drift — the claim this product makes is that there is a single
// record, and two implementations of it would quietly falsify that.
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type ArrearsCase } from '../../lib/api'
import { ageOf, CATEGORIES, money, ROLE_LABELS } from '../../lib/format'
import Touchpoints from './Touchpoints'

type Detail = Awaited<ReturnType<typeof api.ams.case>>

export default function AmsInquiry() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [params, setParams] = useSearchParams()
  const caseId = params.get('case')
  const [q, setQ] = useState(params.get('q') ?? '')
  const [hits, setHits] = useState<ArrearsCase[] | null>(null)
  const [d, setD] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Opening a case clears the result list: the answer has been found, and leaving the search
  // results underneath a full history is two screens fighting for the same attention.
  useEffect(() => {
    if (!org || !caseId) { setD(null); return }
    setD(null); setError(null)
    api.ams.case(org, caseId)
      .then(setD)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }, [org, caseId])

  async function search(e?: React.FormEvent) {
    e?.preventDefault()
    if (!org || q.trim().length < 2) return
    setBusy(true); setError(null); setHits(null)
    try {
      const r = await api.ams.search(org, q.trim())
      setHits(r.cases)
      // Exactly one match is not ambiguous, so do not make the agent click it while the resident
      // waits. More than one, or none, still needs a human to choose.
      const only = r.cases.length === 1 ? r.cases[0] : undefined
      if (only) setParams({ case: only.id, q: q.trim() })
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err))
    } finally { setBusy(false) }
  }

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Resident inquiry</h2>
          <p className="muted">
            Every contact with a household, from every team, in one place. Search by name, account
            number or address.
          </p>
        </div>
      </header>

      <form className="card inq-search" onSubmit={search}>
        <input value={q} onChange={e => setQ(e.target.value)}
               placeholder="e.g. Ong, or Blk 317, or NS005497"
               aria-label="Search by name, account number or address" />
        <button className="btn pri" type="submit" disabled={busy || q.trim().length < 2}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <div className="page-state page-state-error">{error}</div>}

      {hits && !caseId && (
        <section className="card">
          <h3>{hits.length} match{hits.length === 1 ? '' : 'es'}</h3>
          {hits.length === 0 && (
            <p className="muted sm">
              Nobody found for “{q}”. Try the block number, or part of the surname. This is a real
              result, not a failed search.
            </p>
          )}
          <ul className="plain inq-hits">
            {hits.map(c => (
              <li key={c.id}>
                <button className="inq-hit" onClick={() => setParams({ case: c.id, q })}>
                  <div>
                    <b>{c.residentName}</b>
                    <div className="muted xs">{c.address} · {c.accountNo}</div>
                  </div>
                  <div className="inq-hit-right">
                    <b>{money(c.balanceCents)}</b>
                    <div className="muted xs">
                      {c.daysOverdue} days · {CATEGORIES[c.category ?? 'unsegmented']}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {caseId && !d && !error && <div className="page-state">Loading…</div>}

      {d && (
        <>
          <section className="card">
            <div className="inq-head">
              <div>
                <h3>{d.case.residentName}</h3>
                <div className="muted sm">
                  {d.case.address} · Division {d.case.division} · {d.case.accountNo}
                </div>
              </div>
              <div className="actions">
                {hits && hits.length > 1 && (
                  <button className="btn" onClick={() => setParams({ q })}>Back to results</button>
                )}
                <Link className="btn pri" to={`/ams/cases/${d.case.id}`}>Open the case record</Link>
              </div>
            </div>
            <div className="grid-4 inq-facts">
              <div>
                <div className="kpi-label">Owed</div>
                <b>{money(d.live.balanceCents)}</b>
                <div className="muted xs">TCMS, read {ageOf(d.live.asOf)}</div>
              </div>
              <div>
                <div className="kpi-label">Days overdue</div>
                <b>{d.live.daysOverdue}</b>
                <div className="muted xs">since the oldest unpaid charge</div>
              </div>
              <div>
                <div className="kpi-label">Category</div>
                <b>{CATEGORIES[d.case.category ?? 'unsegmented']}</b>
                <div className="muted xs">{d.case.hardshipCode ?? '—'}</div>
              </div>
              <div>
                <div className="kpi-label">Last person to reach out</div>
                <b>{lastHuman(d.events) ?? 'Nobody yet'}</b>
              </div>
            </div>
          </section>

          <Touchpoints events={d.events} />
        </>
      )}
    </div>
  )
}

/** The last HUMAN contact — an automated nudge is not somebody reaching out, and answering this
 *  question with "Automated outreach, yesterday" would mislead an officer about whether this
 *  household has actually heard from a person. */
function lastHuman(events: Detail['events']): string | null {
  const e = events.find(x => x.actorName)
  if (!e) return null
  const role = e.actorRole ? ROLE_LABELS[e.actorRole] ?? e.actorRole : null
  return `${e.actorName}${role ? ` · ${role}` : ''}`
}
